import {employeePortalOrgSlugs, employeePortalErrorMessages} from '@/constants/employee-portal.js'
import {joinPaths} from '@/helpers/url.js'
import {useDataStore} from '@/store/data.store.js'
import {useRouting} from '@/composables/use-routing.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {useLocalStorage} from '@/composables/use-local-storage.js'
import {storeToRefs} from 'pinia'
import {computed, watchEffect} from 'vue'

/**
 * Helper composable to check if the current organization uses the employee portal
 * and other related logic
 */
export function useEmployeePortal() {

  //Composables
  const {getFromLocalStorage, setInLocalStorage} = useLocalStorage()
  const {railsApiBaseUrl} = useRailsUrls()
  const {route, queryParams} = useRouting()
  const store = useDataStore()

  //Org sourced flag
  const {organization} = storeToRefs(store)
  const orgUsesEmployeePortal = computed(() => organization.value?.usesEmployeePortal)

  //Route sourced flag
  const routeUsesEmployeePortal = computed(() => Boolean(route?.meta?.isEmployeePortal))

  //Organization slug and flags for white labelling
  const slug = computed(() => queryParams.value?.slug)
  const isSlugValid = computed(() => Object.values(employeePortalOrgSlugs).includes(slug.value))
  const isCoats = computed(() => employeePortalOrgSlugs.COATS === slug.value)

  //Login endpoint URL and error message
  const loginEndpointUrl = computed(() => {
    const base = joinPaths(railsApiBaseUrl.value, 'employee/login')
    if (slug.value) {
      return `${base}?slug=${slug.value}`
    }
    return base
  })
  const loginErrorMessage = computed(() => {
    const code = queryParams.value?.error
    if (!code) {
      return null
    }
    return employeePortalErrorMessages[code] ?? 'An error occurred. Please try again.'
  })

  //Store in local storage
  watchEffect(() => {
    if (orgUsesEmployeePortal.value || routeUsesEmployeePortal.value) {
      setInLocalStorage('usesEmployeePortal', true)
    }
  })

  //Resolved value
  const usesEmployeePortal = computed(() => {
    if (routeUsesEmployeePortal.value || orgUsesEmployeePortal.value) {
      return true
    }
    return getFromLocalStorage('usesEmployeePortal', false)
  })

  //Expose interface
  return {
    usesEmployeePortal,
    loginEndpointUrl,
    loginErrorMessage,
    slug,
    isSlugValid,
    isCoats,
  }
}
